import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    Grid,
    TextField,
    CircularProgress,
    Alert,
    Divider,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stepper,
    Step,
    StepLabel,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    IconButton,
    ThemeProvider,
    createTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PaymentIcon from '@mui/icons-material/Payment';
import SchoolIcon from '@mui/icons-material/School';

// Ensure API_URL ends with /api
const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;
const SSO_URL = process.env.REACT_APP_SSO_URL || 'http://localhost:9000';

const PAYMENT_UNAVAILABLE = 'Für dieses Angebot ist derzeit kein Bezahlvorgang möglich. Es wurde kein Betrag abgebucht. Bitte wenden Sie sich an den Anbieter dieser Seite.';

interface LocationState {
    landingPage: {
        id: number;
        slug: string;
        title: string;
        companyId: number;
        requireRegistration: boolean;
        primaryColor: string | null;
        products: Array<{
            id: number;
            title: string;
            prices: Array<{
                id: number;
                priceType: string;
                amount: string;
                currency: string;
                formattedPrice: string;
            }>;
        }>;
    };
    selectedProducts: number[];
}

interface PaymentProvider {
    id: string;
    name: string;
    enabled: boolean;
}

const GuestCheckout: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const state = location.state as LocationState | null;

    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [paymentProviders, setPaymentProviders] = useState<PaymentProvider[]>([]);

    // Form data
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        confirmPassword: '',
        acceptTerms: false,
        acceptPrivacy: false,
    });

    // Order state
    const [order, setOrder] = useState<{
        uuid: string;
        orderNumber: string;
        total: string;
        currency: string;
        isFree: boolean;
    } | null>(null);

    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    // Redirect if no state
    useEffect(() => {
        if (!state || !state.landingPage || state.selectedProducts.length === 0) {
            navigate(`/p/${slug}`);
        }
    }, [state, slug, navigate]);

    // Load payment providers
    useEffect(() => {
        const loadProviders = async () => {
            if (!slug) return;
            try {
                const response = await fetch(`${API_URL}/public/landing-pages/${slug}/payment-providers`);
                if (response.ok) {
                    const data = await response.json();
                    setPaymentProviders(data.providers || []);
                }
            } catch (err) {
                console.error('Error loading payment providers:', err);
            }
        };
        loadProviders();
    }, [slug]);

    if (!state) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <CircularProgress />
            </Container>
        );
    }

    const { landingPage, selectedProducts } = state;

    const theme = createTheme({
        palette: {
            primary: {
                main: landingPage.primaryColor || '#1976d2',
            },
        },
    });

    const getSelectedProductsWithPrices = () => {
        return selectedProducts.map(productId => {
            const product = landingPage.products.find(p => p.id === productId);
            const price = product?.prices.find(p => p.priceType !== 'subscription') || product?.prices[0];
            return { product, price };
        }).filter(item => item.product);
    };

    const calculateTotal = () => {
        return getSelectedProductsWithPrices().reduce((sum, { price }) => {
            return sum + (price ? parseFloat(price.amount) : 0);
        }, 0);
    };

    const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));

        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const validateRegistration = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.email || !formData.email.includes('@')) {
            errors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
        }
        if (!formData.firstName || formData.firstName.trim().length < 2) {
            errors.firstName = 'Bitte geben Sie Ihren Vornamen ein';
        }
        if (!formData.lastName || formData.lastName.trim().length < 2) {
            errors.lastName = 'Bitte geben Sie Ihren Nachnamen ein';
        }
        if (!formData.password || formData.password.length < 12) {
            errors.password = 'Passwort muss mindestens 12 Zeichen haben';
        }
        if (formData.password !== formData.confirmPassword) {
            errors.confirmPassword = 'Passwörter stimmen nicht überein';
        }
        if (!formData.acceptTerms) {
            errors.acceptTerms = 'Bitte akzeptieren Sie die AGB';
        }
        if (!formData.acceptPrivacy) {
            errors.acceptPrivacy = 'Bitte akzeptieren Sie die Datenschutzerklärung';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmitRegistration = async () => {
        if (!validateRegistration()) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}/public/landing-pages/${slug}/checkout`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: formData.email,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    password: formData.password,
                    productIds: selectedProducts,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || data.error || 'Der Kauf konnte nicht abgeschlossen werden.');
            }

            setOrder(data.order);

            // If order is free, process immediately
            if (data.order.isFree) {
                await processFreeOrder(data.order.uuid, formData.email);
            } else {
                setActiveStep(1);
            }
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Der Kauf konnte nicht abgeschlossen werden.';
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const processFreeOrder = async (orderUuid: string, email: string) => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/public/landing-pages/orders/${orderUuid}/free`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(data.detail || data.error || 'Der Kauf konnte nicht abgeschlossen werden.');
            }

            setActiveStep(2);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Der Kauf konnte nicht abgeschlossen werden.';
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const handlePayPalPayment = async () => {
        if (!order) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}/public/landing-pages/orders/${order.uuid}/paypal/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: formData.email }),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok || !data.approvalUrl) {
                throw new Error(data.detail || data.error || PAYMENT_UNAVAILABLE);
            }

            window.location.href = data.approvalUrl;
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : PAYMENT_UNAVAILABLE;
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const handleBackToLandingPage = () => {
        navigate(`/p/${slug}`);
    };

    const handleGoToCourses = () => {
        // Redirect to login page with email pre-filled
        window.location.href = `${SSO_URL}/login?email=${encodeURIComponent(formData.email)}`;
    };

    const total = calculateTotal();
    const currency = landingPage.products[0]?.prices[0]?.currency || 'EUR';
    const steps = total > 0
        ? ['Registrierung', 'Bezahlung', 'Bestätigung']
        : ['Registrierung', 'Bestätigung'];
    const stepperStep = total > 0 ? activeStep : Math.min(activeStep, 1);

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                if (total > 0) {
                    return (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Kauf derzeit nicht möglich
                            </Typography>
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                {PAYMENT_UNAVAILABLE}
                            </Alert>
                            <Button startIcon={<ArrowBackIcon />} onClick={handleBackToLandingPage}>
                                Zurück zur Seite
                            </Button>
                        </Box>
                    );
                }

                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Ihre Daten
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {error}
                            </Alert>
                        )}

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    label="E-Mail-Adresse"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange('email')}
                                    error={!!validationErrors.email}
                                    helperText={validationErrors.email}
                                    required
                                    disabled={loading}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Vorname"
                                    value={formData.firstName}
                                    onChange={handleChange('firstName')}
                                    error={!!validationErrors.firstName}
                                    helperText={validationErrors.firstName}
                                    required
                                    disabled={loading}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Nachname"
                                    value={formData.lastName}
                                    onChange={handleChange('lastName')}
                                    error={!!validationErrors.lastName}
                                    helperText={validationErrors.lastName}
                                    required
                                    disabled={loading}
                                />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Für den Zugang zum Kurs wird ein Konto angelegt. Bitte wählen Sie ein Passwort.
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Passwort"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={handleChange('password')}
                                    error={!!validationErrors.password}
                                    helperText={validationErrors.password || 'Mindestens 12 Zeichen'}
                                    required
                                    disabled={loading}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Passwort bestätigen"
                                    type="password"
                                    value={formData.confirmPassword}
                                    onChange={handleChange('confirmPassword')}
                                    error={!!validationErrors.confirmPassword}
                                    helperText={validationErrors.confirmPassword}
                                    required
                                    disabled={loading}
                                />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Divider sx={{ my: 1 }} />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.acceptTerms}
                                            onChange={handleChange('acceptTerms')}
                                            disabled={loading}
                                        />
                                    }
                                    label="Ich akzeptiere die AGB"
                                />
                                {validationErrors.acceptTerms && (
                                    <Typography variant="caption" color="error" display="block">
                                        {validationErrors.acceptTerms}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.acceptPrivacy}
                                            onChange={handleChange('acceptPrivacy')}
                                            disabled={loading}
                                        />
                                    }
                                    label="Ich akzeptiere die Datenschutzerklärung"
                                />
                                {validationErrors.acceptPrivacy && (
                                    <Typography variant="caption" color="error" display="block">
                                        {validationErrors.acceptPrivacy}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                            <Button
                                startIcon={<ArrowBackIcon />}
                                onClick={handleBackToLandingPage}
                                disabled={loading}
                            >
                                Zurück
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmitRegistration}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : undefined}
                            >
                                Kostenlos starten
                            </Button>
                        </Box>
                    </Box>
                );

            case 1:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Zahlungsmethode wählen
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                {error}
                            </Alert>
                        )}

                        <Grid container spacing={2}>
                            {paymentProviders.filter(p => p.enabled).map(provider => (
                                <Grid size={{ xs: 12, sm: 6 }} key={provider.id}>
                                    <Card
                                        sx={{
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            '&:hover': { boxShadow: 4 },
                                        }}
                                        onClick={provider.id === 'paypal' ? handlePayPalPayment : undefined}
                                    >
                                        <CardContent sx={{ textAlign: 'center', py: 4 }}>
                                            {provider.id === 'paypal' ? (
                                                <Box
                                                    component="img"
                                                    src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-100px.png"
                                                    alt="PayPal"
                                                    sx={{ height: 40, mb: 2 }}
                                                />
                                            ) : (
                                                <LockIcon sx={{ fontSize: 48, mb: 2, color: 'primary.main' }} />
                                            )}
                                            <Typography variant="h6">
                                                {provider.name}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 4 }}>
                            <Button
                                startIcon={<ArrowBackIcon />}
                                onClick={() => setActiveStep(0)}
                                disabled={loading}
                            >
                                Zurück
                            </Button>
                        </Box>
                    </Box>
                );

            case 2:
                return (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                        <Typography variant="h4" gutterBottom>
                            Vielen Dank!
                        </Typography>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Ihre Bestellung war erfolgreich.
                        </Typography>

                        {order && (
                            <Paper sx={{ p: 3, my: 4, maxWidth: 400, mx: 'auto' }}>
                                <Typography variant="body1">
                                    Bestellnummer: <strong>{order.orderNumber}</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Eine Bestätigung wurde an {formData.email} gesendet.
                                </Typography>
                            </Paper>
                        )}

                        <Alert severity="info" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
                            Ihr Zugang wurde erstellt. Sie können sich jetzt mit Ihrer E-Mail-Adresse und dem gewählten Passwort anmelden.
                        </Alert>

                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<SchoolIcon />}
                            onClick={handleGoToCourses}
                        >
                            Zum Kurs
                        </Button>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 4 }}>
                <Container maxWidth="lg">
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h4" gutterBottom>
                            {landingPage.title}
                        </Typography>
                        <Stepper activeStep={stepperStep} sx={{ mb: 4 }}>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>

                    <Grid container spacing={4}>
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Card>
                                <CardContent sx={{ p: 4 }}>
                                    {renderStepContent()}
                                </CardContent>
                            </Card>
                        </Grid>

                        {activeStep < 2 && (
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Paper sx={{ p: 3 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Ihre Bestellung
                                    </Typography>

                                    <List dense>
                                        {getSelectedProductsWithPrices().map(({ product, price }) => (
                                            product && (
                                                <ListItem key={product.id}>
                                                    <ListItemIcon>
                                                        <CheckCircleIcon color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={product.title}
                                                        secondary={price?.formattedPrice}
                                                    />
                                                </ListItem>
                                            )
                                        ))}
                                    </List>

                                    <Divider sx={{ my: 2 }} />

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="h6">Gesamt</Typography>
                                        <Typography variant="h6" color="primary">
                                            {new Intl.NumberFormat('de-DE', {
                                                style: 'currency',
                                                currency: currency,
                                            }).format(total)}
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default GuestCheckout;
